/* eslint-disable react-refresh/only-export-components -- imperative module: exports showAnnouncement/dismissAnnouncement helpers alongside an internal card component; not an HMR boundary. */
import { toast } from 'sonner';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { getOrCreateSessionId } from '@lib/analytics/session';
// Module-scope (non-React) analytics: emit via amplitude.track directly with a hand-picked slice
// of the base payload, as MaintenanceTakeover does. The useAmplitudeTracking hook needs React.
import { amplitude } from '@lib/amplitude';
import { getPlatform } from '@lib/platform';
import { isPwaInstalled } from '@lib/pwa/install-utils';
import { awaitCelebrationIdle } from '@utils/celebrationMarker';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';

/**
 * A branded announcement for news from Meetball itself (a new version, a feature that just
 * shipped), built on sonner's `toast.custom` like `showBanner` in ./banner.tsx.
 *
 * It reuses the app's mounted `<Toaster />` (no new portal/provider), never shows over a
 * celebration drawer, and tracks its own analytics so callers never add them per call.
 * Design rules: meetball-skills design-ui-ux component-patterns.md, "Announcements".
 */
export interface AnnouncementAction {
  label: string;
  onClick: () => void;
}

export interface AnnouncementOptions {
  /**
   * Required. Showing the same id replaces rather than stacks, and it is the analytics key.
   * Re-showing an id while its card is still animating out (~200ms) is not supported: sonner
   * merges the new toast into the closing one, as it does for showBanner.
   */
  id: string;
  /** Illustration shown in a white circle on the left. Defaults to the mascot. */
  illustrationSrc?: string;
  title: string;
  body: string;
  /** White pill button. Runs `onClick`, then the announcement dismisses. */
  primary: AnnouncementAction;
  /** Optional white text button. Runs `onClick`, then the announcement dismisses. */
  secondary?: AnnouncementAction;
  /** Runs when the announcement closes without a button tap (swipe away, or duration ends). */
  onDismiss?: () => void;
  /** Auto-dismiss delay in milliseconds. Default: stays until tapped. */
  durationMs?: number;
}

const DEFAULT_ILLUSTRATION_SRC = '/signup-mascot.svg';

/**
 * The current show for each id, waiting behind a celebration or on screen. Aborting it means the
 * app took that show down (a button tap, dismissAnnouncement, or a newer show with the same id):
 * a pending wait never renders, and the close that follows is not counted as a dismissal.
 */
const currentShows = new Map<string, AbortController>();

function trackAnnouncement(eventName: string, properties: Record<string, unknown>): void {
  // Analytics must never break the announcement itself (storage helpers can throw).
  try {
    const platform = getPlatform();
    amplitude.track(eventName, {
      session_id: getOrCreateSessionId(),
      path: window.location.pathname || null,
      platform: platform === 'web' ? (isPwaInstalled() ? 'pwa' : 'web') : platform,
      app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null,
      ...properties,
    });
  } catch (error) {
    logger.error('Failed to track announcement event', eventName, sanitizeError(error));
  }
}

interface AnnouncementCardProps {
  illustrationSrc: string;
  title: string;
  body: string;
  primary: AnnouncementAction;
  secondary?: AnnouncementAction;
  onAction: (cta: 'primary' | 'secondary', action: AnnouncementAction) => void;
}

const FOCUS_RING_CLASS =
  'outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-mb-teal-ink';

function AnnouncementCard({
  illustrationSrc,
  title,
  body,
  primary,
  secondary,
  onAction,
}: AnnouncementCardProps) {
  return (
    <div className="flex w-full items-start gap-3 rounded-2xl bg-mb-teal-ink p-4 text-white shadow-lg">
      <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-white">
        <img src={illustrationSrc} alt="" className="size-9 object-contain" />
      </div>

      <div className="min-w-0 flex-1">
        <p className="font-livvic text-lg font-bold leading-tight">{title}</p>
        <p className="mt-1 font-fira text-sm leading-snug">{body}</p>

        <div className="mt-3 flex flex-nowrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              onAction('primary', primary);
            }}
            className={`min-h-11 shrink-0 rounded-full bg-white px-5 font-fira text-sm font-semibold text-mb-teal-ink transition-colors active:bg-white/90 ${FOCUS_RING_CLASS}`}
          >
            {primary.label}
          </button>
          {secondary && (
            <button
              type="button"
              onClick={() => {
                onAction('secondary', secondary);
              }}
              className={`min-h-11 shrink-0 rounded-full px-4 font-fira text-sm font-medium text-white transition-colors active:bg-white/15 [@media(hover:hover)]:hover:bg-white/10 ${FOCUS_RING_CLASS}`}
            >
              {secondary.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function renderAnnouncement(opts: AnnouncementOptions, show: AbortController): void {
  const { id } = opts;
  // A visible toast with this id is replaced in place: the same impression, not a new one.
  const replacing = toast.getToasts().some(t => t.id === id);

  // sonner calls onDismiss for a swipe and also for our own toast.dismiss(); the abort tells
  // them apart.
  const handleClose = () => {
    if (currentShows.get(id) === show) currentShows.delete(id);
    if (show.signal.aborted) return;
    trackAnnouncement(ANALYTICS_EVENTS.announcement_dismissed, { announcement_id: id });
    opts.onDismiss?.();
  };

  toast.custom(
    () => (
      <AnnouncementCard
        illustrationSrc={opts.illustrationSrc ?? DEFAULT_ILLUSTRATION_SRC}
        title={opts.title}
        body={opts.body}
        primary={opts.primary}
        secondary={opts.secondary}
        onAction={(cta, action) => {
          // The card stays tappable through sonner's exit animation; only the first tap counts.
          if (show.signal.aborted) return;
          show.abort();
          trackAnnouncement(ANALYTICS_EVENTS.announcement_cta_clicked, {
            announcement_id: id,
            cta,
          });
          try {
            action.onClick();
          } finally {
            toast.dismiss(id);
          }
        }}
      />
    ),
    {
      id,
      duration: opts.durationMs ?? Infinity,
      // The card paints its own teal box; strip the Toaster's default white card around it.
      className: 'w-full !border-0 !bg-transparent !p-0 !shadow-none',
      onDismiss: handleClose,
      onAutoClose: handleClose,
    },
  );

  if (!replacing) {
    trackAnnouncement(ANALYTICS_EVENTS.announcement_shown, { announcement_id: id });
  }
}

/**
 * Show an announcement at the top of the screen.
 *
 * Tapping a button runs its `onClick` and then dismisses. Showing the same `id` again while it
 * is on screen replaces it in place, right away. If a celebration owns the screen, a new
 * announcement waits until it closes, and `announcement_shown` fires only once it is visible.
 */
export function showAnnouncement(opts: AnnouncementOptions): void {
  currentShows.get(opts.id)?.abort();
  const show = new AbortController();
  currentShows.set(opts.id, show);
  // A card already on screen is updated in place right away; only a new card waits.
  if (toast.getToasts().some(t => t.id === opts.id)) {
    renderAnnouncement(opts, show);
    return;
  }
  void awaitCelebrationIdle(show.signal).then(() => {
    if (show.signal.aborted) return;
    renderAnnouncement(opts, show);
  });
}

/**
 * Dismiss an announcement by id, including one still waiting behind a celebration.
 * Counts as closed by the app, so it fires no `announcement_dismissed` and no `onDismiss`.
 */
export function dismissAnnouncement(id: string): void {
  const show = currentShows.get(id);
  if (!show) return;
  show.abort();
  currentShows.delete(id);
  toast.dismiss(id);
}
